import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, RefreshCw, AlertCircle, CheckCircle, Clock, RotateCcw, Trash2, ExternalLink } from "lucide-react"
import { Button, Card, CardBody, CardHeader, Chip, Spinner, Divider, Select, SelectItem, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from "@heroui/react"
import { useGetTaskById, useUpdateTaskStatus, useDeleteTask } from "@src/presentation/hooks/task"
import { formatDateHuman, formatDateFull } from "@src/shared/utils/date-formatter"
import { ToastService } from "@src/shared/services/toast.service"

const statusColorMap = {
    pending: "warning",
    processing: "primary",
    completed: "success",
    failed: "danger",
} as const;

const statusIconMap = {
    pending: Clock,
    processing: RefreshCw,
    completed: CheckCircle,
    failed: AlertCircle,
} as const;

export default function TaskDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [statusChangeModal, setStatusChangeModal] = useState<{ isOpen: boolean; newStatus: string | null }>({ isOpen: false, newStatus: null })
    const [deleteModal, setDeleteModal] = useState(false)

    const { data: task, isLoading, error, refetch } = useGetTaskById(id || "")
    const { mutate: updateStatus, isPending: isUpdating } = useUpdateTaskStatus({
        onSuccess: () => {
            setStatusChangeModal({ isOpen: false, newStatus: null })
            refetch()
            ToastService.success("Task status has been updated successfully")
        },
        onError: (error: Error) => {
            ToastService.error(error.message || "Failed to update task status")
        }
    })
    const { mutate: deleteTask, isPending: isDeleting } = useDeleteTask({
        onSuccess: () => {
            setDeleteModal(false)
            ToastService.success("Task has been deleted successfully")
            navigate("/tasks")
        },
        onError: (error: Error) => {
            ToastService.error(error.message || "Failed to delete task")
        }
    })

    const handleStatusChangeClick = (newStatus: string) => {
        if (task && newStatus !== task.status) {
            setStatusChangeModal({ isOpen: true, newStatus })
        }
    }

    const confirmStatusChange = () => {
        if (task && statusChangeModal.newStatus) {
            updateStatus({
                taskId: task.id,
                updateData: { status: statusChangeModal.newStatus as any }
            })
        }
    }

    const handleDeleteClick = () => {
        setDeleteModal(true)
    }

    const confirmDelete = () => {
        if (task) {
            deleteTask(task.id)
        }
    }

    const getStatusLabel = (status: string) => {
        return status.charAt(0).toUpperCase() + status.slice(1)
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
            </div>
        )
    }

    if (error || !task) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <AlertCircle className="h-12 w-12 text-danger mb-4" />
                <h2 className="text-xl font-semibold mb-2">Task Not Found</h2>
                <p className="text-default-500 mb-4">
                    {error ? error.message : "The requested task could not be found."}
                </p>
                <Button onPress={() => navigate("/tasks")} className="rounded border border-border">
                    Back to Tasks
                </Button>
            </div>
        )
    }

    const StatusIcon = statusIconMap[task.status];

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                isIconOnly
                                variant="light"
                                onPress={() => navigate("/tasks")}
                                className="flex-shrink-0"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <h1 className="text-xl font-semibold text-foreground">Task Details</h1>
                            <Chip
                                color={statusColorMap[task.status]}
                                variant="flat"
                                startContent={<StatusIcon className="h-3 w-3" />}
                            >
                                {task.status}
                            </Chip>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Button
                                color="default"
                                variant="flat"
                                onPress={() => refetch()}
                                isLoading={isLoading}
                                startContent={<RefreshCw className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Refresh</span>
                                <span className="sm:hidden">Refresh</span>
                            </Button>
                            <Button
                                color="danger"
                                variant="flat"
                                onPress={handleDeleteClick}
                                startContent={<Trash2 className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Delete</span>
                                <span className="sm:hidden">Delete</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Basic Information */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="pb-3">
                                <h2 className="text-lg font-semibold">Basic Information</h2>
                            </CardHeader>
                            <Divider />
                            <CardBody className="space-y-4">
                                <div>
                                    <p className="text-sm text-default-500">Task ID</p>
                                    <code className="text-sm font-mono bg-default-100 px-2 py-1 rounded">
                                        {task.id}
                                    </code>
                                </div>
                                <div>
                                    <p className="text-sm text-default-500">PASCO ID</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <code className="text-sm font-mono bg-default-100 px-2 py-1 rounded">
                                            {task.pascoId}
                                        </code>
                                        <Button
                                            size="sm"
                                            variant="flat"
                                            onPress={() => navigate(`/pascos/${task.pascoId}`)}
                                            startContent={<ExternalLink className="h-3 w-3" />}
                                            className="rounded"
                                        >
                                            View PASCO
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm text-default-500">Attempt Count</p>
                                    <Chip
                                        size="md"
                                        variant="flat"
                                        color={task.attemptCount > 3 ? "danger" : task.attemptCount > 1 ? "warning" : "default"}
                                        className="mt-1"
                                    >
                                        {task.attemptCount} {task.attemptCount === 1 ? "attempt" : "attempts"}
                                    </Chip>
                                </div>
                                <div>
                                    <p className="text-sm text-default-500">Created At</p>
                                    <p className="text-sm font-medium mt-1">{formatDateFull(task.createdAt)}</p>
                                    <p className="text-xs text-default-400 mt-0.5">{formatDateHuman(task.createdAt)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-default-500">Last Updated</p>
                                    <p className="text-sm font-medium mt-1">{formatDateFull(task.updatedAt)}</p>
                                    <p className="text-xs text-default-400 mt-0.5">{formatDateHuman(task.updatedAt)}</p>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Error Information */}
                        {task.errorMessage && (
                            <Card className="border-l-4 border-l-danger border border-border rounded shadow-none">
                                <CardHeader className="pb-3">
                                    <div className="flex items-center gap-2">
                                        <AlertCircle className="h-5 w-5 text-danger" />
                                        <h2 className="text-lg font-semibold">Error Details</h2>
                                    </div>
                                </CardHeader>
                                <Divider />
                                <CardBody>
                                    <pre className="text-sm text-danger whitespace-pre-wrap break-words bg-danger-50 dark:bg-danger-50/10 p-4 rounded">
                                        {task.errorMessage}
                                    </pre>
                                </CardBody>
                            </Card>
                        )}
                    </div>

                    {/* Actions Sidebar */}
                    <div className="space-y-6">
                        {/* Status Control */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="pb-3">
                                <h2 className="text-lg font-semibold">Status Control</h2>
                            </CardHeader>
                            <Divider />
                            <CardBody className="space-y-4">
                                <Select
                                    label="Change Status"
                                    placeholder="Select new status"
                                    selectedKeys={[task.status]}
                                    onChange={(e) => handleStatusChangeClick(e.target.value)}
                                    classNames={{
                                        trigger: "rounded border border-border",
                                        popoverContent: "rounded border border-border"
                                    }}
                                >
                                    <SelectItem key="pending" className="rounded">Pending</SelectItem>
                                    <SelectItem key="processing" className="rounded">Processing</SelectItem>
                                    <SelectItem key="completed" className="rounded">Completed</SelectItem>
                                    <SelectItem key="failed" className="rounded">Failed</SelectItem>
                                </Select>

                                {task.status === "failed" && (
                                    <Button
                                        color="warning"
                                        fullWidth
                                        onPress={() => handleStatusChangeClick("pending")}
                                        startContent={<RotateCcw className="h-4 w-4" />}
                                        className="rounded"
                                    >
                                        Reschedule Task
                                    </Button>
                                )}
                            </CardBody>
                        </Card>

                        {/* Quick Actions */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="pb-3">
                                <h2 className="text-lg font-semibold">Quick Actions</h2>
                            </CardHeader>
                            <Divider />
                            <CardBody className="space-y-2">
                                <Button
                                    fullWidth
                                    variant="flat"
                                    onPress={() => navigate(`/pascos/${task.pascoId}`)}
                                    startContent={<ExternalLink className="h-4 w-4" />}
                                    className="rounded"
                                >
                                    View Related PASCO
                                </Button>
                                <Button
                                    fullWidth
                                    variant="flat"
                                    onPress={() => navigate("/tasks")}
                                    className="rounded"
                                >
                                    View All Tasks
                                </Button>
                                <Button
                                    fullWidth
                                    variant="flat"
                                    onPress={() => navigate("/tasks/failed")}
                                    className="rounded"
                                >
                                    View Failed Tasks
                                </Button>
                            </CardBody>
                        </Card>

                        {/* Task Stats */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="pb-3">
                                <h2 className="text-lg font-semibold">Task Statistics</h2>
                            </CardHeader>
                            <Divider />
                            <CardBody className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-default-500">Status</span>
                                    <Chip
                                        size="sm"
                                        color={statusColorMap[task.status]}
                                        variant="flat"
                                    >
                                        {task.status}
                                    </Chip>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-default-500">Attempts</span>
                                    <span className="text-sm font-medium">{task.attemptCount}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-default-500">Has Error</span>
                                    <span className="text-sm font-medium">
                                        {task.errorMessage ? "Yes" : "No"}
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </main>

            {/* Status Change Confirmation Modal */}
            <Modal
                isOpen={statusChangeModal.isOpen}
                onClose={() => setStatusChangeModal({ isOpen: false, newStatus: null })}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1 border-b border-border">
                        <h3 className="text-lg font-semibold">Confirm Status Change</h3>
                    </ModalHeader>
                    <ModalBody className="py-6">
                        <div className="space-y-4">
                            <p className="text-sm text-default-600">
                                Are you sure you want to change the task status?
                            </p>
                            {statusChangeModal.newStatus && (
                                <div className="rounded border border-border bg-default-50 p-4 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-default-500">Current Status:</span>
                                        <Chip size="sm" color={statusColorMap[task.status]} variant="flat">
                                            {getStatusLabel(task.status)}
                                        </Chip>
                                    </div>
                                    <div className="flex items-center justify-center py-2">
                                        <ArrowLeft className="h-4 w-4 text-default-400 -rotate-90" />
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-default-500">New Status:</span>
                                        <Chip
                                            size="sm"
                                            color={statusColorMap[statusChangeModal.newStatus as keyof typeof statusColorMap]}
                                            variant="flat"
                                        >
                                            {getStatusLabel(statusChangeModal.newStatus)}
                                        </Chip>
                                    </div>
                                    {statusChangeModal.newStatus === "pending" && task.status === "failed" && (
                                        <div className="mt-3 p-3 rounded border border-warning/20 bg-warning/5">
                                            <p className="text-xs text-warning">
                                                <strong>Note:</strong> This will reschedule the failed task for re-processing.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter className="border-t border-border">
                        <Button
                            variant="light"
                            onPress={() => setStatusChangeModal({ isOpen: false, newStatus: null })}
                            className="rounded"
                        >
                            Cancel
                        </Button>
                        <Button
                            color={statusChangeModal.newStatus === "pending" ? "warning" : "primary"}
                            onPress={confirmStatusChange}
                            isLoading={isUpdating}
                            className="rounded"
                        >
                            Confirm Change
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={deleteModal}
                onClose={() => setDeleteModal(false)}
                classNames={{
                    base: "rounded border border-border",
                    backdrop: "bg-black/50"
                }}
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1 border-b border-border">
                        <h3 className="text-lg font-semibold text-danger">Confirm Deletion</h3>
                    </ModalHeader>
                    <ModalBody className="py-6">
                        <div className="space-y-4">
                            <p className="text-sm text-default-600">
                                Are you sure you want to <strong className="text-danger">permanently delete</strong> this task? This action cannot be undone.
                            </p>
                            <div className="rounded border border-danger/20 bg-danger/5 p-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-default-500">Task ID:</span>
                                    <code className="text-xs">{task.id.substring(0, 16)}...</code>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-default-500">PASCO ID:</span>
                                    <code className="text-xs">{task.pascoId.substring(0, 16)}...</code>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-default-500">Status:</span>
                                    <Chip size="sm" color={statusColorMap[task.status]} variant="flat">
                                        {task.status}
                                    </Chip>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-default-500">Attempts:</span>
                                    <span>{task.attemptCount}</span>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                    <ModalFooter className="border-t border-border">
                        <Button
                            variant="light"
                            onPress={() => setDeleteModal(false)}
                            className="rounded"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            onPress={confirmDelete}
                            isLoading={isDeleting}
                            className="rounded"
                        >
                            Delete Task
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}
